import React, { useEffect, useRef, useState } from 'react';
import { Star, Sparkles, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { isNativeMobile, nativePlatform, purchase as iapPurchase, initIap, IAP_SKUS } from '../lib/iap';

// Cashfree pack_id → store SKU. Kept alongside the UI that consumes it;
// backend has the same table.
const PACK_TO_SKU = {
  pack_starter:   IAP_SKUS.starter,
  pack_explorer:  IAP_SKUS.explorer,
  pack_voyager:   IAP_SKUS.voyager,
  pack_cosmonaut: IAP_SKUS.cosmonaut,
  pack_galactic:  IAP_SKUS.galactic,
  pack_supreme:   IAP_SKUS.supreme,
};

export default function BuyStars({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [packs, setPacks] = useState([]);
  const [provider, setProvider] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [cfEnv, setCfEnv] = useState('sandbox');
  const [loadingId, setLoadingId] = useState(null);
  const cfRef = useRef(null); // cached Cashfree SDK instance
  const native = isNativeMobile();
  const platform = nativePlatform();

  // Load pack catalogue + Cashfree SDK when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/payments/packs');
        if (cancelled) return;
        setPacks(data.packs || []);
        setProvider(data.provider || 'cashfree');
        setEnabled(!!data.enabled);
        setCfEnv(data.cashfree_env || 'sandbox');
      } catch {
        if (!cancelled) toast.error('Could not load packs');
      }
    })();
    if (native) {
      initIap().catch(() => {});
    } else if (!cfRef.current) {
      loadCashfree({ mode: 'sandbox' })
        .then((cf) => { if (!cancelled) cfRef.current = cf; })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [open, native]);

  // Re-load the SDK if the merchant flips sandbox ↔ production mid-session.
  useEffect(() => {
    if (native || !open) return;
    cfRef.current = null;
    loadCashfree({ mode: cfEnv === 'production' ? 'production' : 'sandbox' })
      .then((cf) => { cfRef.current = cf; })
      .catch(() => {});
  }, [cfEnv, native, open]);

  // Poll our backend after checkout closes so we know when the webhook has
  // credited the Stars. Stops after ~20 seconds of `created` status.
  const pollUntilPaid = async (orderId, packName) => {
    for (let i = 0; i < 12; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1500));
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (data.status === 'paid') {
          toast.success(`+${(data.total_stars || 0).toLocaleString()} ⭐ added to your wallet!`);
          refresh?.();
          onOpenChange?.(false);
          return true;
        }
        if (data.status && data.status !== 'created') {
          toast.error('Payment could not be completed. You can try again.');
          return false;
        }
      } catch { /* keep polling */ }
    }
    toast.message('Payment is still being confirmed. Your Stars will appear once your bank confirms.');
    return false;
  };

  const buy = async (pack) => {
    setLoadingId(pack.id);
    try {
      // Native path: Apple StoreKit / Google Play Billing.
      if (native) {
        const sku = PACK_TO_SKU[pack.id];
        if (!sku) {
          toast.error('This pack is not available in the app yet');
          return;
        }
        await iapPurchase(sku);
        toast.success('Purchase submitted — updating balance…');
        setTimeout(() => { refresh(); onOpenChange(false); }, 1500);
        return;
      }

      // Web path: Cashfree hosted checkout.
      if (!cfRef.current) {
        cfRef.current = await loadCashfree({ mode: cfEnv === 'production' ? 'production' : 'sandbox' });
      }
      const { data: order } = await api.post('/payments/create-order', { pack_id: pack.id });
      if (!order?.payment_session_id) {
        toast.error('Could not start payment. Please try again.');
        return;
      }

      // Close our dialog BEFORE opening Cashfree — otherwise Radix's focus
      // trap intercepts clicks inside the Cashfree iframe and every button
      // in the checkout modal looks "dead" on real devices.
      onOpenChange?.(false);
      // Yield to React so the Dialog fully unmounts before the Cashfree
      // overlay portals into <body>.
      await new Promise((r) => setTimeout(r, 100));

      const result = await cfRef.current.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: '_modal',
        // Return URL is only used if `_modal` is unavailable in the environment
        // (older mobile WebViews). We handle the actual success via polling
        // + our webhook.
        returnUrl: `${window.location.origin}/payment-result?order_id=${encodeURIComponent(order.order_id)}`,
      });

      if (result?.error) {
        toast.error(result.error.message || 'Payment was not completed. You can try again.');
        return;
      }
      if (result?.paymentDetails) {
        // Checkout closed after payment attempt — start polling backend.
        toast.message('Confirming your payment…');
        await pollUntilPaid(order.order_id, pack.name);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not start payment');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white w-[calc(100vw-1rem)] max-w-[420px] sm:max-w-2xl max-h-[92vh] overflow-y-auto scroll-hide p-4 sm:p-6 rounded-2xl" data-testid="buy-stars-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Star size={18} className="text-amber-300" fill="currentColor" />
            Top up Stars
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-white/50 flex items-center gap-2" data-testid="buy-stars-trust-line">
          {native ? (
            <>
              <Smartphone size={12} className="text-emerald-400" />
              {platform === 'ios'
                ? 'Secure purchase via Apple App Store · Face ID / Touch ID'
                : 'Secure purchase via Google Play Billing'}
            </>
          ) : (
            <>
              <ShieldCheck size={12} className="text-emerald-400" />
              Secure payment by Cashfree · UPI · Cards · NetBanking · Wallets
              {cfEnv !== 'production' && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[10px] font-semibold uppercase">Sandbox</span>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
          {packs.map(p => {
            const total = p.stars + p.bonus;
            const busy = loadingId === p.id;
            const isBest = p.badge === 'BEST VALUE';
            const isPopular = p.badge === 'MOST POPULAR';
            const highlighted = isBest || isPopular;
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl p-4 bg-gradient-to-br from-fuchsia-500/10 via-cyan-500/5 to-amber-500/10 border transition flex flex-col ${
                  isBest
                    ? 'border-emerald-400/60 ring-1 ring-emerald-400/30 shadow-[0_0_28px_rgba(16,185,129,0.25)]'
                    : isPopular
                    ? 'border-fuchsia-400/60 ring-1 ring-fuchsia-400/30 shadow-[0_0_28px_rgba(217,70,239,0.30)]'
                    : 'border-white/10 hover:border-fuchsia-400/40'
                }`}
                data-testid={`pack-${p.id}`}
              >
                {p.badge && (
                  <span
                    data-testid={`badge-${p.id}`}
                    className={`absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 text-[10px] font-extrabold tracking-wider rounded-full text-black shadow-lg animate-pulse ${
                      isBest
                        ? 'bg-gradient-to-r from-emerald-300 to-cyan-300'
                        : 'bg-gradient-to-r from-fuchsia-300 to-amber-300'
                    }`}
                  >
                    {p.badge}
                  </span>
                )}
                <div className="text-4xl">{p.emoji}</div>
                <div className="mt-2 font-display font-semibold">{p.name}</div>
                <div className="mt-1 flex items-center gap-1 text-amber-300 font-bold text-xl">
                  <Star size={14} fill="currentColor" />
                  {total.toLocaleString()}
                </div>
                {p.bonus > 0 && (
                  <div className="text-[11px] text-emerald-400">
                    Includes <span className="font-semibold">+{p.bonus.toLocaleString()}</span> bonus ⭐
                  </div>
                )}
                <div className="flex-1" />
                <Button
                  onClick={() => buy(p)}
                  disabled={busy || !enabled}
                  className={`mt-3 text-white border-0 rounded-full ${
                    highlighted
                      ? 'bg-gradient-to-r from-emerald-500 via-cyan-500 to-fuchsia-500 hover:opacity-90'
                      : 'bg-gradient-to-r from-fuchsia-500 to-cyan-500'
                  }`}
                  data-testid={`buy-${p.id}`}
                >
                  {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
                  ₹{p.amount_inr.toLocaleString()}
                </Button>
              </div>
            );
          })}
        </div>

        {!enabled && (
          <div className="text-xs text-rose-300 mt-2">Payments are currently unavailable. Please try again later.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
